<template>
  <div class="ask-page">
    <!-- Hero -->
    <section class="ask-hero" v-if="messages.length === 0">
      <h1 class="ask-headline">
        Ask the supply chain research catalog.
      </h1>
      <p class="ask-subhead">
        Get evidence-based answers synthesized from the academic literature.
        Citations included.
      </p>

      <div class="starter-questions">
        <p class="starter-label">Try asking:</p>
        <button
          v-for="(q, i) in starterQuestions"
          :key="i"
          class="starter-btn"
          @click="askQuestion(q)"
          :disabled="loading"
        >
          {{ q }}
        </button>
      </div>
    </section>

    <!-- New chat button (visible once conversation has started) -->
    <div v-if="messages.length > 0" class="chat-toolbar">
      <button class="new-chat-btn" @click="startNewChat" :disabled="loading">
        <v-icon size="16" class="mr-1">mdi-plus</v-icon>
        <span>New chat</span>
      </button>
    </div>

    <!-- Conversation -->
    <section class="conversation" v-if="messages.length > 0">
      <div
        v-for="(msg, i) in messages"
        :key="i"
        class="message"
        :class="`message--${msg.role}`"
      >
        <div class="message-role">
          {{ msg.role === 'user' ? 'You' : 'Supply Chain Research' }}
        </div>
        <div class="message-content">
          <div v-if="msg.role === 'assistant' && msg.thinking && msg.thinking.length" class="thinking-trace">
            <details>
              <summary>{{ msg.thinking.length }} search step{{ msg.thinking.length === 1 ? '' : 's' }}</summary>
              <ul>
                <li v-for="(t, ti) in msg.thinking" :key="ti">
                  <span class="thinking-tool">{{ t.tool }}</span>
                  <span class="thinking-detail">{{ t.detail }}</span>
                </li>
              </ul>
            </details>
          </div>
          <div class="message-text" v-html="renderMarkdown(msg.content)"></div>
        </div>
      </div>

      <div v-if="loading" class="loading-indicator">
        <span class="loading-dot"></span>
        <span class="loading-dot"></span>
        <span class="loading-dot"></span>
        <span class="loading-status">{{ loadingStatus }}</span>
      </div>
    </section>

    <!-- Input -->
    <section class="input-bar" :class="{ 'input-bar--fixed': messages.length > 0 }">
      <div class="input-wrapper">
        <textarea
          ref="textareaRef"
          v-model="currentInput"
          class="ask-input"
          placeholder="Ask a question about supply chain research..."
          rows="1"
          @keydown.enter.exact.prevent="askQuestion(currentInput)"
          @keydown.shift.enter="() => {}"
          :disabled="loading"
        ></textarea>
        <button
          class="ask-submit"
          @click="askQuestion(currentInput)"
          :disabled="loading || !currentInput.trim()"
          aria-label="Ask"
        >
          <v-icon>mdi-arrow-up</v-icon>
        </button>
      </div>
      <p class="input-hint">
        Answers cite papers from the catalog. Click any citation to open the paper.
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, watch } from 'vue';
import { useHead } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';

defineOptions({ name: 'AskPage' });

useHead({
  title: 'Ask SCR',
  titleTemplate: undefined,
});

const route = useRoute();
const router = useRouter();

onMounted(() => {
  // If the URL has ?q=...  (from the main search bar in AI mode), auto-submit
  const initialQ = route.query.q;
  if (initialQ && typeof initialQ === 'string' && initialQ.trim()) {
    askQuestion(initialQ);
  }
});

// Also react when the URL query changes (in-page nav)
watch(() => route.query.q, (q) => {
  if (q && typeof q === 'string' && q.trim()) {
    // Only re-ask if it's a different question than what's already shown
    const lastUserMsg = messages.value.filter(m => m.role === 'user').slice(-1)[0];
    if (!lastUserMsg || lastUserMsg.content !== q.trim()) {
      askQuestion(q);
    }
  }
});

const ASK_API = (() => {
  // Dev: ask server runs on localhost:8091
  // Prod: TODO - point to the deployed ask service URL
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8091';
  }
  // Same host different port for now
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.hostname}:8091`;
  }
  return '';
})();

const messages = ref([]);
const currentInput = ref('');
const loading = ref(false);
const loadingStatus = ref('Thinking...');
const textareaRef = ref(null);

const QUESTION_POOL = [
  'What does Gartner say about WMS vendor leadership?',
  'Which SAP customers achieved measurable demand planning improvements?',
  'What are the key success factors for WMS implementations?',
  'How does pick path optimization impact warehouse productivity?',
  'How do companies measure supply chain resilience?',
  'What does Martin Christopher say about value-adding networks?',
  'How does lean thinking apply to transportation management?',
  'What are the main challenges in last mile delivery?',
  'Which AI techniques work best for demand forecasting?',
  'What ROI have companies seen from TMS implementations?',
  'How are companies using automation in warehouse operations?',
  'What does research say about inventory optimization with machine learning?',
  'How do 3PLs select and implement WMS platforms?',
  'What is the role of digital twins in supply chain planning?',
  'How does blockchain apply to supply chain traceability?',
  'What are best practices for warehouse slotting optimization?',
  'How do companies approach omnichannel order fulfillment?',
  'What vendor solutions exist for supply chain visibility?',
  'How has e-commerce changed warehouse design requirements?',
  'What does Yossi Sheffi say about balancing green and profitability?',
];

function pickRandom(arr, n) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

const starterQuestions = pickRandom(QUESTION_POOL, 5);

function startNewChat() {
  if (loading.value) return;
  // Confirm if there's a substantive conversation
  if (messages.value.length >= 2) {
    if (!window.confirm('Start a new chat? The current conversation will be cleared.')) {
      return;
    }
  }
  messages.value = [];
  currentInput.value = '';
  loadingStatus.value = '';
  // Clean up URL params so we don't accidentally re-trigger the old question
  if (route.query.q) {
    router.replace({ name: 'Ask', query: {} });
  }
  // Focus the input ready for the next question
  nextTick(() => {
    if (textareaRef.value) textareaRef.value.focus();
  });
}

function askQuestion(text) {
  const question = (text || '').trim();
  if (!question || loading.value) return;

  // Push user message
  messages.value.push({ role: 'user', content: question });
  currentInput.value = '';
  loading.value = true;
  loadingStatus.value = 'Thinking...';

  // Placeholder assistant message that we'll fill in
  const assistantMsg = {
    role: 'assistant',
    content: '',
    thinking: [],
  };
  messages.value.push(assistantMsg);

  // Build history for the API (exclude the placeholder)
  const history = messages.value
    .slice(0, -1)
    .filter(m => m.content)
    .map(m => ({ role: m.role, content: m.content }));
  // Drop the just-added user message from history (it goes in `message`)
  const apiHistory = history.slice(0, -1);

  streamAnswer(question, apiHistory, assistantMsg);
}

async function streamAnswer(message, history, assistantMsg) {
  try {
    const res = await fetch(`${ASK_API}/api/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, history }),
    });

    if (!res.ok || !res.body) {
      assistantMsg.content = `Error: Could not reach the research catalog (${res.status}).`;
      loading.value = false;
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const dataStr = line.slice(6);
        if (!dataStr.trim()) continue;
        try {
          const event = JSON.parse(dataStr);
          handleEvent(event, assistantMsg);
        } catch {
          // Skip malformed events
        }
      }
    }
  } catch (e) {
    assistantMsg.content = `Error: ${e.message}`;
  } finally {
    loading.value = false;
    await nextTick();
    scrollToBottom();
  }
}

function handleEvent(event, assistantMsg) {
  if (event.type === 'thinking') {
    loadingStatus.value = event.text || 'Thinking...';
  } else if (event.type === 'tool_call') {
    const detail = event.args?.query
      ? `"${event.args.query}"`
      : event.args?.id || event.args?.search || '';
    assistantMsg.thinking.push({
      tool: event.tool,
      detail,
    });
    loadingStatus.value = `Calling ${event.tool}...`;
  } else if (event.type === 'tool_result') {
    if (assistantMsg.thinking.length > 0) {
      const last = assistantMsg.thinking[assistantMsg.thinking.length - 1];
      last.result = event.result_summary;
    }
    loadingStatus.value = event.result_summary || 'Reading results...';
  } else if (event.type === 'final_answer') {
    assistantMsg.content = event.text || '';
    // Pre-resolve SCR source URLs so citation cards have working links
    const scrSlugs = [...(event.text || '').matchAll(/scr:\s*([a-z0-9_]+)/gi)];
    if (scrSlugs.length > 0) {
      const sources = scrSlugs.map(m => ({ kind: 'scr', slug: m[1] }));
      preResolveScrSources(sources).then(() => {
        // Force re-render so the source cards pick up the resolved URLs
        assistantMsg.content = event.text || '';
      });
    }
    nextTick(scrollToBottom);
  } else if (event.type === 'error') {
    assistantMsg.content = `Error: ${event.message}`;
  } else if (event.type === 'done') {
    loadingStatus.value = '';
  }
}

function scrollToBottom() {
  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
}

// Cache for resolved SCR slugs → source URLs
const scrUrlCache = new Map();

async function resolveAndCacheScrUrl(slug) {
  if (scrUrlCache.has(slug)) return scrUrlCache.get(slug);
  try {
    const resp = await fetch(`${ASK_API}/api/scr-content/${slug}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    const url = data.source_url || null;
    if (url) scrUrlCache.set(slug, url);
    return url;
  } catch {
    return null;
  }
}

// Pre-resolve all SCR slugs in a set of sources so links are ready before user clicks
async function preResolveScrSources(sources) {
  const scrSources = sources.filter(s => s.kind === 'scr' && s.slug);
  await Promise.allSettled(scrSources.map(s => resolveAndCacheScrUrl(s.slug)));
}

function renderSourceCard(s, n) {
  if (s.kind === 'academic') {
    return `
      <a href="/works/${s.id}" target="_blank" rel="noopener" class="source-card source-card--academic">
        <div class="source-card-number">${n}</div>
        <div class="source-card-body">
          <div class="source-card-kind">Academic paper</div>
          <div class="source-card-title">${escapeHtml(s.title)}</div>
          <div class="source-card-meta">
            <span class="source-card-year">${s.year}</span>
            <span class="source-card-id">${s.id}</span>
          </div>
        </div>
        <div class="source-card-arrow">↗</div>
      </a>
    `;
  }
  // SCR content card - use pre-resolved URL if available, otherwise a resolver link
  const resolvedUrl = scrUrlCache.get(s.slug);
  const href = resolvedUrl || `${ASK_API}/api/scr-content/${s.slug}/redirect`;
  return `
    <a href="${escapeHtml(href)}"
       target="_blank" rel="noopener"
       class="source-card source-card--scr">
      <div class="source-card-number">${n}</div>
      <div class="source-card-body">
        <div class="source-card-kind">Vendor content</div>
        <div class="source-card-title">${escapeHtml(s.title)}</div>
        <div class="source-card-meta">
          ${s.meta ? `<span class="source-card-year">${escapeHtml(s.meta)}</span>` : ''}
        </div>
      </div>
      <div class="source-card-arrow">↗</div>
    </a>
  `;
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderMarkdown(text) {
  if (!text) return '';

  let html = text;

  // Escape HTML first
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Find the Sources section first so we can render it specially
  // Match "**Sources:**" or "Sources:" followed by numbered list
  const sourcesMatch = html.match(/(\*\*Sources:\*\*|Sources:)\s*\n([\s\S]+?)$/);
  let mainBody = html;
  let sourcesBlock = '';
  let sources = [];

  if (sourcesMatch) {
    mainBody = html.slice(0, sourcesMatch.index).trim();
    const sourcesText = sourcesMatch[2].trim();

    // Parse each source line. Two formats:
    //   Academic: "1. [Title] (Year) - id: w12345"
    //   SCR:      "1. [Title] (Vendor, Year) - scr: slug_here"  (year optional)
    const lines = sourcesText.split('\n').filter(l => l.trim());
    sources = lines.map(line => {
      // Academic paper match
      // Uses greedy (.+) to handle titles with parens/colons inside them.
      // Matches the LAST (year) before " - id: wXXXXX"
      let m = line.match(/^(?:\d+\.\s*)?(.*?)\(.*?(\d{4})\)\s*[-\u2013\u2014]\s*id:\s*(w\d+)/i);
      if (m) {
        // Clean title: strip leading [ and trailing ]
        let title = m[1].trim().replace(/^\[/, '').replace(/\]\s*$/, '').trim();
        return {
          kind: 'academic',
          title,
          year: m[2],
          id: m[3].toUpperCase(),
        };
      }
      // SCR content match - vendor and year in parens, optional
      m = line.match(/^(?:\d+\.\s*)?(.*?)\(([^)]*)\)\s*[-\u2013\u2014]\s*scr:\s*([a-z0-9_]+)/i);
      if (m) {
        let title = m[1].trim().replace(/^\[/, '').replace(/\]\s*$/, '').trim();
        const meta = m[2].trim();
        return {
          kind: 'scr',
          title,
          meta,
          slug: m[3],
        };
      }
      // SCR without parens
      m = line.match(/^(?:\d+\.\s*)?(.*?)\s*[-\u2013\u2014]\s*scr:\s*([a-z0-9_]+)/i);
      if (m) {
        let title = m[1].trim().replace(/^\[/, '').replace(/\]\s*$/, '').trim();
        return { kind: 'scr', title, meta: '', slug: m[2] };
      }
      return null;
    }).filter(Boolean);

    if (sources.length > 0) {
      // Build a lookup so inline citations can link to the right source.
      // Map "year" and key words from each source title/meta to the URL.
      for (const s of sources) {
        if (s.kind === 'academic' && s.id) {
          s._url = `/works/${s.id}`;
        } else if (s.kind === 'scr' && s.slug) {
          const resolved = scrUrlCache.get(s.slug);
          s._url = resolved || `${ASK_API}/api/scr-content/${s.slug}/redirect`;
        }
      }

      sourcesBlock = `<div class="sources-section">
        <div class="sources-heading">Cited sources</div>
        <div class="sources-list">
          ${sources.map((s, i) => renderSourceCard(s, i + 1)).join('')}
        </div>
      </div>`;
    }
  }

  // Now render the main body
  html = mainBody;

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3 class="md-h3">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="md-h2">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="md-h1">$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*([^\*]+?)\*/g, '<em>$1</em>');

  // Inline citations [Author, Year] - link to the matching source if we can find it.
  // The sources array from the bottom section has URLs; match by year + fuzzy title/author.
  html = html.replace(/\[([A-Z][^\]]+?,\s*(\d{4})[^\]]*?)\]/g, (match, citText, year) => {
    // Try to find a matching source by year + any word overlap
    if (sources && sources.length > 0) {
      const citWords = citText.toLowerCase().split(/[\s,]+/).filter(w => w.length > 2);
      let bestMatch = null;
      let bestScore = 0;
      for (const s of sources) {
        const sYear = s.year || (s.meta && s.meta.match(/(\d{4})/) ? s.meta.match(/(\d{4})/)[1] : null);
        if (sYear && sYear !== year) continue; // Year must match
        // Count word overlap with title
        const titleWords = (s.title || '').toLowerCase().split(/[\s,]+/);
        const overlap = citWords.filter(w => titleWords.some(tw => tw.includes(w) || w.includes(tw))).length;
        if (overlap > bestScore) {
          bestScore = overlap;
          bestMatch = s;
        }
      }
      // If year matched a source (even with 0 word overlap), link to it
      if (!bestMatch) {
        // Fallback: just match on year alone
        bestMatch = sources.find(s => {
          const sYear = s.year || (s.meta && s.meta.match(/(\d{4})/) ? s.meta.match(/(\d{4})/)[1] : null);
          return sYear === year;
        });
      }
      if (bestMatch && bestMatch._url) {
        return `<a href="${escapeHtml(bestMatch._url)}" target="_blank" rel="noopener" class="inline-citation inline-citation--linked">[${citText}]</a>`;
      }
    }
    return `<span class="inline-citation">[${citText}]</span>`;
  });

  // Work ID anywhere in text (e.g. "id: W12345") - link to detail page
  html = html.replace(/\bid:\s*(W\d+)\b/gi, (match, id) => {
    return `<a href="/works/${id}" class="md-citation-link" target="_blank">${id.toUpperCase()}</a>`;
  });

  // Numbered lists
  html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="md-li-num">$2</li>');
  html = html.replace(/(<li class="md-li-num">.*?<\/li>(?:\n<li class="md-li-num">.*?<\/li>)*)/gs, '<ol class="md-ol">$1</ol>');

  // Bullet lists
  html = html.replace(/^[-•*]\s+(.+)$/gm, '<li class="md-li">$1</li>');
  html = html.replace(/(<li class="md-li">.*?<\/li>(?:\n<li class="md-li">.*?<\/li>)*)/gs, '<ul class="md-ul">$1</ul>');

  // Paragraphs (double newline)
  html = html.split(/\n\n+/).map(block => {
    if (block.match(/^<(h[1-6]|ul|ol|li)/)) return block;
    if (!block.trim()) return '';
    return `<p class="md-p">${block.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');

  return html + sourcesBlock;
}
</script>

<style scoped lang="scss">
.ask-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 48px 24px 200px;
  min-height: calc(100vh - 60px);
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.ask-page :deep(*) {
  font-family: inherit;
}

// Hero
.ask-hero {
  margin-top: 80px;
  margin-bottom: 60px;
}

.ask-headline {
  font-size: 48px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #0A0A0A;
  margin: 0 0 16px;
}

.ask-subhead {
  font-size: 18px;
  line-height: 1.6;
  color: #52525B;
  margin: 0 0 40px;
}

.starter-questions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 32px;
}

.starter-label {
  font-size: 13px;
  font-weight: 500;
  color: #818982;
  margin: 0 0 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.starter-btn {
  text-align: left;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #e6e8e6;
  border-radius: 8px;
  font-size: 15px;
  color: #041708;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: #f4f4f5;
    border-color: #d2cbfe;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// New chat button toolbar
.chat-toolbar {
  position: sticky;
  top: 16px;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  margin-top: -32px;
  margin-bottom: 24px;
  pointer-events: none;
}

.new-chat-btn {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e6e8e6;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #041708;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    border-color: #d2cbfe;
    background: rgba(210, 203, 254, 0.15);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Conversation
.conversation {
  margin-bottom: 40px;
}

.message {
  margin-bottom: 32px;
}

.message-role {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
}

.message--user {
  .message-role {
    color: #6B5CE7;
  }
  .message-content {
    background: #f4f4f5;
    border-left: 3px solid #d2cbfe;
    padding: 16px 20px;
    border-radius: 0 8px 8px 0;
    font-size: 16px;
    color: #041708;
  }
}

.message--assistant {
  .message-role {
    color: #041708;
  }
  .message-content {
    color: #041708;
    line-height: 1.7;
  }
}

.thinking-trace {
  margin-bottom: 16px;
  font-size: 13px;
  color: #818982;

  details {
    cursor: pointer;
  }

  summary {
    color: #6B5CE7;
    user-select: none;
    padding: 6px 0;

    &:hover {
      color: #5A4BD6;
    }
  }

  ul {
    list-style: none;
    padding: 8px 0 8px 16px;
    margin: 0;
    border-left: 2px solid #d2cbfe;
  }

  li {
    padding: 4px 0;
  }

  .thinking-tool {
    font-family: monospace;
    font-size: 12px;
    color: #041708;
    background: #d2cbfe;
    padding: 2px 6px;
    border-radius: 3px;
    margin-right: 8px;
  }
}

// Markdown render
:deep(.md-h1) {
  font-size: 28px;
  font-weight: 700;
  margin: 24px 0 16px;
  color: #041708;
}
:deep(.md-h2) {
  font-size: 22px;
  font-weight: 600;
  margin: 24px 0 12px;
  color: #041708;
  border-bottom: 1px solid #e6e8e6;
  padding-bottom: 8px;
}
:deep(.md-h3) {
  font-size: 18px;
  font-weight: 600;
  margin: 20px 0 8px;
  color: #041708;
}
:deep(.md-p) {
  margin: 12px 0;
}
:deep(.md-ul), :deep(.md-ol) {
  margin: 12px 0;
  padding-left: 24px;
}
:deep(.md-li), :deep(.md-li-num) {
  margin: 6px 0;
}
:deep(.md-citation-link) {
  display: inline-block;
  background: #d2cbfe;
  color: #041708 !important;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    background: #B8AFFE;
  }
}

:deep(.inline-citation) {
  display: inline;
  color: #6B5CE7;
  font-weight: 500;
  font-size: 0.92em;
  background: rgba(210, 203, 254, 0.25);
  padding: 1px 4px;
  border-radius: 3px;
}

:deep(.inline-citation--linked) {
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(210, 203, 254, 0.5);
    text-decoration: underline;
  }
}

// Sources section - rich card list
:deep(.sources-section) {
  margin-top: 40px;
  padding-top: 24px;
  border-top: 2px solid #d2cbfe;
}

:deep(.sources-heading) {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #6B5CE7;
  margin-bottom: 12px;
}

:deep(.sources-list) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:deep(.source-card) {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #e6e8e6;
  border-radius: 8px;
  text-decoration: none !important;
  color: #041708 !important;
  transition: all 0.15s;

  &:hover {
    border-color: #d2cbfe;
    background: rgba(210, 203, 254, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(107, 92, 231, 0.08);
  }
}

:deep(.source-card-number) {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background: #d2cbfe;
  color: #041708;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

:deep(.source-card-body) {
  flex: 1;
  min-width: 0;
}

:deep(.source-card-title) {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #041708;
  margin-bottom: 4px;
}

:deep(.source-card-meta) {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: #818982;
}

:deep(.source-card-year) {
  font-weight: 500;
}

:deep(.source-card-id) {
  font-family: monospace;
  background: #f4f4f5;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

:deep(.source-card-arrow) {
  flex-shrink: 0;
  font-size: 16px;
  color: #6B5CE7;
  align-self: center;
  transition: transform 0.15s;
}

:deep(.source-card:hover .source-card-arrow) {
  transform: translate(2px, -2px);
}

// Variant: Academic (purple accent)
:deep(.source-card--academic .source-card-number) {
  background: #d2cbfe;
}
:deep(.source-card--academic) {
  border-left: 3px solid #d2cbfe;
}

// Variant: SCR vendor content (lime accent)
:deep(.source-card--scr .source-card-number) {
  background: #cdfc8a;
}
:deep(.source-card--scr) {
  border-left: 3px solid #cdfc8a;
}
:deep(.source-card--scr:hover) {
  border-color: #cdfc8a;
  background: rgba(205, 252, 138, 0.08);
  box-shadow: 0 2px 8px rgba(160, 200, 80, 0.12);
}
:deep(.source-card--scr:hover .source-card-arrow) {
  color: #6B9B2A;
}

// Kind label (Academic paper / Vendor content)
:deep(.source-card-kind) {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6B5CE7;
  margin-bottom: 2px;
}
:deep(.source-card--scr .source-card-kind) {
  color: #5A7820;
}

// Loading
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 0;
  color: #818982;
  font-size: 14px;
}

.loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6B5CE7;
  animation: bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
}

.loading-status {
  margin-left: 12px;
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

// Input
.input-bar {
  position: relative;

  &--fixed {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #ffffff;
    border-top: 1px solid #e6e8e6;
    padding: 16px 24px;
    z-index: 100;

    .input-wrapper {
      max-width: 800px;
      margin: 0 auto;
    }

    .input-hint {
      max-width: 800px;
      margin: 8px auto 0;
    }
  }
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #d2cbfe;
  border-radius: 12px;
  padding: 12px 12px 12px 16px;
  box-shadow: 0 0 0 4px rgba(210, 203, 254, 0.15);
  transition: box-shadow 0.15s;

  &:focus-within {
    box-shadow: 0 0 0 4px rgba(210, 203, 254, 0.35);
    border-color: #B8AFFE;
  }
}

.ask-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  background: transparent;
  color: #041708;
  min-height: 24px;
  max-height: 200px;
  line-height: 1.5;

  &::placeholder {
    color: #818982;
  }
}

.ask-submit {
  background: #041708;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #0a2e12;
  }

  &:disabled {
    background: #e6e8e6;
    cursor: not-allowed;
    color: #818982;
  }
}

.input-hint {
  font-size: 12px;
  color: #818982;
  margin: 8px 0 0;
  text-align: center;
}

// Responsive
@media (max-width: 600px) {
  .ask-page {
    padding: 24px 16px 180px;
  }
  .ask-headline {
    font-size: 32px;
  }
  .ask-hero {
    margin-top: 32px;
  }
}
</style>
